// SEQ-199: conditional formatting bands, generic and configurable.
//
// A rule maps a measure to an ordered list of bands. A band is a lower bound
// (inclusive; null means "everything below the next band"), a label and a
// colour. A value lands in the band with the highest lower bound that is at or
// below it. So the default Profit % set reads:
//
//   Negative  (no lower bound)  red      value < 0
//   Low       min 0             orange   0 <= value < 10
//   Fair      min 10            yellow   10 <= value < 20
//   Strong    min 20            green    value >= 20
//
// Boundaries belong to the band ABOVE them: exactly 0 is Low, exactly 10 is
// Fair, exactly 20 is Strong (proved in bands.test.ts at -0.01, 0, 9.99, 10,
// 19.99, 20).
//
// COLOURS ARE A FIXED PALETTE, NOT FREE HEX. Each colour is a background and a
// text colour chosen as a pair that clears WCAG AA (4.5:1) for body text
// (proved in bands.test.ts from the stylesheet), so an editor can never save a band whose figures
// cannot be read. Colour is never the only signal either: every band has a
// label, shown in a legend and read out to assistive technology.
//
// Pure TypeScript. The caller decides which measures a rule applies to and
// whether it applies at all (the grid switches rules on money measures off
// under Safe Mode, because a band is a sign and a size hint).

export type BandColour = "red" | "orange" | "yellow" | "green" | "blue" | "grey";

export const BAND_COLOURS: readonly BandColour[] = ["red", "orange", "yellow", "green", "blue", "grey"];

/** Display names. The colours themselves are tokens in PnlWidgetCharts.css (--pnl-band-<colour>-bg and -text), a pair per colour proved to clear 4.5:1 by bands.test.ts. */
export const BAND_COLOUR_NAMES: Record<BandColour, string> = {
  red: "Red",
  orange: "Orange",
  yellow: "Yellow",
  green: "Green",
  blue: "Blue",
  grey: "Grey",
};

export interface FormatBand {
  /** Inclusive lower bound, or null for "no lower bound". Only the lowest band may be null. */
  min: number | null;
  label: string;
  colour: BandColour;
}

export interface FormatRule {
  /** The measure key this rule colours, e.g. "profitPercent". */
  measure: string;
  bands: FormatBand[];
}

/** Josh's default for a percentage margin (SEQ-199, 6 Oct): negative red, 0 to under 10 orange, 10 to under 20 yellow, 20 and over green. */
export const DEFAULT_PERCENT_BANDS: FormatBand[] = [
  { min: null, label: "Negative", colour: "red" },
  { min: 0, label: "Low", colour: "orange" },
  { min: 10, label: "Fair", colour: "yellow" },
  { min: 20, label: "Strong", colour: "green" },
];

export function isBandColour(value: unknown): value is BandColour {
  return typeof value === "string" && (BAND_COLOURS as readonly string[]).includes(value);
}

/** Sorted ascending by lower bound, the null band first. Does not mutate the input. */
export function sortBands(bands: readonly FormatBand[]): FormatBand[] {
  return [...bands].sort((a, b) => {
    if (a.min === null && b.min === null) return 0;
    if (a.min === null) return -1;
    if (b.min === null) return 1;
    return a.min - b.min;
  });
}

const MIDDLE_MARKERS: readonly string[] = ["◇", "◆", "□", "■", "○", "●"];

/**
 * A visible, non-colour marker for a band, by its position from lowest to
 * highest: a down triangle for the lowest, an up triangle for the highest and
 * distinct shapes between, so a band reads the same in grayscale or with
 * colour-vision deficiency. A lone band gets a filled circle.
 */
export function bandMarker(bands: readonly FormatBand[], band: FormatBand): string {
  const sorted = sortBands(bands);
  const index = sorted.findIndex((b) => b.min === band.min && b.label === band.label && b.colour === band.colour);
  if (sorted.length <= 1 || index < 0) return "●";
  if (index === 0) return "▼";
  if (index === sorted.length - 1) return "▲";
  return MIDDLE_MARKERS[(index - 1) % MIDDLE_MARKERS.length];
}

/**
 * The band a value falls in, or null when there is no value, no band, or the
 * value is below the lowest bound of a set whose lowest band has one (then
 * nothing claims it, so it is uncoloured rather than forced into a band).
 */
export function classifyBand(value: number | null | undefined, bands: readonly FormatBand[]): FormatBand | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  let match: FormatBand | null = null;
  for (const band of sortBands(bands)) {
    if (band.min === null || value >= band.min) match = band;
    else break;
  }
  return match;
}

/** Why a band list cannot be saved, or null. Used by the schema validator and the editor. */
export function bandsProblem(bands: unknown): string | null {
  if (!Array.isArray(bands)) return "Bands must be a list.";
  if (bands.length === 0) return "A rule needs at least one band.";
  if (bands.length > 8) return "A rule can have at most 8 bands.";
  let nullCount = 0;
  const seen = new Set<number>();
  for (const band of bands) {
    if (typeof band !== "object" || band === null) return "Each band must be filled in.";
    const b = band as Record<string, unknown>;
    if (b.min !== null && (typeof b.min !== "number" || !Number.isFinite(b.min))) {
      return "Give each band a starting number, or leave only the first band without one.";
    }
    if (b.min === null) nullCount += 1;
    else if (seen.has(b.min as number)) return "Two bands start at the same number. Give each band a different starting number.";
    else seen.add(b.min as number);
    if (typeof b.label !== "string" || b.label.trim() === "" || b.label.length > 40) {
      return "Give each band a label of 1 to 40 characters.";
    }
    if (!isBandColour(b.colour)) return "Pick one of the listed colours for each band.";
  }
  if (nullCount > 1) return "Only the first band can have no starting number.";
  return null;
}

/** Which band fields are at fault, by band position, so an editor marks only those inputs rather than the whole rule. */
export function badBandFields(bands: readonly FormatBand[]): { min: Set<number>; label: Set<number> } {
  const min = new Set<number>();
  const label = new Set<number>();
  const firstAt = new Map<number, number>();
  bands.forEach((band, index) => {
    if (band.min !== null) {
      if (!Number.isFinite(band.min)) min.add(index);
      else if (firstAt.has(band.min)) {
        min.add(index);
        min.add(firstAt.get(band.min) as number);
      } else firstAt.set(band.min, index);
    }
    if (typeof band.label !== "string" || band.label.trim() === "" || band.label.length > 40) label.add(index);
  });
  return { min, label };
}

/** A short range description per band for the legend, in the measure's own unit suffix ("%" or ""). */
export function describeBands(bands: readonly FormatBand[], unitSuffix: string): { label: string; range: string; colour: BandColour; marker: string }[] {
  const sorted = sortBands(bands);
  const fmt = (n: number) => `${Number.isInteger(n) ? n : Number(n.toFixed(2))}${unitSuffix}`;
  return sorted.map((band, index) => {
    const next = sorted[index + 1];
    const upper = next && next.min !== null ? next.min : null;
    let range: string;
    if (band.min === null) range = upper === null ? "All values" : `Under ${fmt(upper)}`;
    else if (upper === null) range = `${fmt(band.min)} and over`;
    else range = `${fmt(band.min)} to under ${fmt(upper)}`;
    return { label: band.label, range, colour: band.colour, marker: bandMarker(bands, band) };
  });
}
